import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ChevronRight, Truck, RotateCcw, CreditCard, Car, Mail, MessageSquare } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const FAQ = [
  {
    q: "Comment trouver la bonne pièce pour mon véhicule ?",
    a: "Saisissez votre plaque d'immatriculation sur la page d'accueil ou dans « Recherche par véhicule » : nous identifions automatiquement votre véhicule et affichons les pièces compatibles. Vous pouvez aussi rechercher par marque, modèle ou référence."
  },
  {
    q: "Quels sont les délais et frais de livraison ?",
    a: "Vos commandes sont préparées et expédiées rapidement. Les frais de livraison et le seuil de livraison offerte sont indiqués dans votre panier avant paiement. Consultez la page Livraison & retours pour le détail."
  },
  {
    q: "Comment suivre ma commande ?",
    a: "Dès l'expédition, vous recevez un email de confirmation avec le transporteur et le numéro de suivi de votre colis. Vous retrouvez également le statut de vos commandes dans votre espace client."
  },
  {
    q: "Comment retourner un article ?",
    a: "Rendez-vous dans votre espace client, section « Mes commandes », et lancez une demande de retour. Notre service client vous accompagne pour l'échange ou le remboursement. Voir la page Livraison & retours."
  },
  {
    q: "Quels moyens de paiement acceptez-vous ?",
    a: "Le paiement est 100 % sécurisé par carte bancaire (CB) et via PayPal. Vos informations de paiement ne sont jamais stockées sur nos serveurs."
  },
  {
    q: "J'ai oublié mon mot de passe, que faire ?",
    a: "Sur la page de connexion, cliquez sur « Mot de passe oublié ». Vous recevrez un email avec un lien sécurisé (valable 1 heure) pour définir un nouveau mot de passe."
  },
];

const QUICK = [
  { icon: Car, label: "Trouver mes pièces", desc: "Recherche par plaque ou modèle", to: "/vehicule" },
  { icon: Truck, label: "Livraison & retours", desc: "Délais, frais et retours", to: "/livraison-retours" },
  { icon: RotateCcw, label: "Mes commandes", desc: "Suivi et historique", to: "/compte" },
  { icon: CreditCard, label: "Paiement sécurisé", desc: "CB & PayPal", to: "/conditions-generales" },
];

const AidePage = () => (
  <div data-testid="aide-page">
    <section className="hero-swoosh">
      <div className="max-w-[1000px] mx-auto px-5 pt-8 pb-8 relative z-10">
        <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-4">
          <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
          <ChevronRight size={15} />
          <span className="text-slate-50 font-semibold">Besoin d'aide ?</span>
        </div>
        <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-5xl mb-2 flex items-center gap-3">
          <HelpCircle className="text-[#FF3B3B]" size={40} /> Besoin d'aide&nbsp;?
        </h1>
        <p className="text-slate-400">Retrouvez les réponses aux questions les plus fréquentes, ou contactez notre équipe.</p>
      </div>
    </section>

    <section className="max-w-[1000px] mx-auto px-5 py-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {QUICK.map((item) => (
          <Link key={item.to} to={item.to} data-testid={`aide-quick-${item.to.replace(/\//g, "")}`}
            className="group bg-[#10131A] rounded-2xl border border-[#1E232D] p-5 hover:border-[#E01F1F] hover:shadow-md transition-all">
            <div className="w-11 h-11 rounded-xl bg-[#E01F1F]/10 flex items-center justify-center mb-3 group-hover:bg-[#E01F1F] transition-colors">
              <item.icon size={22} className="text-[#FF3B3B] group-hover:text-white transition-colors" />
            </div>
            <div className="font-bold text-slate-50 text-[15px]">{item.label}</div>
            <div className="text-[13px] text-slate-400">{item.desc}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-xl md:text-2xl mb-3">Questions fréquentes</h2>
          <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] px-5 md:px-7">
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`aide-faq-${i}`}>
                  <AccordionTrigger className="text-[15px] text-slate-50 font-semibold">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-[14px] text-slate-300 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#10131A] rounded-2xl p-6 text-white space-y-4 sticky top-28">
            <div className="w-11 h-11 rounded-xl bg-[#E01F1F] flex items-center justify-center"><MessageSquare size={22} /></div>
            <div className="font-extrabold italic uppercase tracking-tight text-xl">Une autre question&nbsp;?</div>
            <p className="text-[14px] text-white/70">Notre équipe vous répond sous 24h ouvrées. Écrivez-nous, nous sommes là pour vous aider.</p>
            <Link to="/contact" data-testid="aide-contact-btn" className="btn-orange rounded-lg px-6 py-3 font-bold flex items-center justify-center gap-2">
              <Mail size={18} /> Nous contacter
            </Link>
            <div className="text-[13px] text-white/60 pt-3 border-t border-white/10 flex items-center gap-2">
              <Mail size={15} className="text-[#FF3B3B]" /> contact@ktsauto.fr
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default AidePage;
